import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertController, ModalController, NavParams } from '@ionic/angular';
import { Subject, firstValueFrom, map, takeUntil, tap } from 'rxjs';
import { AgreementFormStep4Component } from 'src/app/pages/agreements/components/agreement-form/steps/agreement-form-step4/agreement-form-step4.component';
import { AgreementInterruptiveModalComponent } from 'src/app/pages/agreements/components/agreement-interruptive-modal/agreement-interruptive-modal.component';
import { AgreementRatesFormComponent } from 'src/app/pages/agreements/components/create-update-agreement/agreement-form/agreement-rates-form/agreement-rates-form.component';
import { MABLE_CLIENT_FEES } from 'src/app/pages/agreements/constants/mable-fees.constants';
import { DEFAULT_RATE_SCHEDULE } from 'src/app/pages/agreements/constants/rate-schedule.constants';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import {
  AgreementRatesFormOutput,
  AgreementTravelFormOutput,
  RateType,
} from 'src/app/pages/agreements/types/agreement-create-update-form.model';
import { AvailableAndDefaultBudgetSupportTypes } from 'src/app/pages/agreements/types/available-and-default-budget-support-types';
import { ExceededRate } from 'src/app/pages/agreements/types/exceeded-rate';
import { ProfileSearchService } from 'src/app/pages/search/profile-search.service';
import { TermsAndConditionsComponent } from 'src/app/shared/components/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { GetBudgetSupportTypesListQueryService } from 'src/app/shared/graphql/agreements/get-budget-support-types-query.graphql.autogenerated';
import { Agreement } from 'src/app/shared/models/gql/agreement.model';
import { Organisation } from 'src/app/shared/models/gql/organisation.model';
import { Rate } from 'src/app/shared/models/gql/rate.model';
import { WorkerProfile } from 'src/app/shared/models/gql/worker-profile.model';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { MinimumRateService } from 'src/app/shared/services/minimum-rate.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';

/**
 * @deprecated - Please make updates in the relevant section components, i.e.
 * AgreementRatesFormComponent, AgreementTravelFormComponent, etc instead.
 */

@Component({
  selector: 'app-agreement-form-step3',
  templateUrl: './agreement-form-step3.component.html',
  styleUrls: ['./agreement-form-step3.component.scss'],
})
export class AgreementFormStep3Component implements OnInit, OnDestroy {
  /** get the rates child component */
  @ViewChild(AgreementRatesFormComponent) ratesForm: AgreementRatesFormComponent;

  private statesForNew: Array<string> = ['requested', '', null, undefined];

  form: UntypedFormGroup;

  /** Whether the form has been submitted */
  hasSubmitted = false;

  /** Whether the rates are valid */
  isRatesFormValid = false;

  /** Class members */
  public agreement: Agreement = null;

  // stores agreement status. True if it is a new agreement and false if it isn't.
  public isNewAgreement: boolean;

  // worker profile
  profileObject: WorkerProfile;

  // flag to check whether to show info related to budget support type (the rate info banner, budget support type items)
  shouldShowBudgetSupportTypeInformation = false;

  // organisationTerms object
  organisationTerms: Organisation = null;

  // check if agreement is for private job or public job
  isPrivateJob = false;

  /** Subject used to unsubcribe from the service FormGroup status chagne. */
  private destroySub$ = new Subject();

  /** minimum agreement rate allowed in the platform */
  minimumRate: number;

  // Budget support types list
  budgetSupportTypes: AvailableAndDefaultBudgetSupportTypes = {
    availableBudgetSupportTypes: [],
    defaultBudgetSupportTypes: [],
  };

  constructor(
    protected fb: UntypedFormBuilder,
    protected modalCtrl: ModalController,
    protected alertCtrl: AlertController,
    protected loaderService: LoaderService,
    private profileSearchService: ProfileSearchService,
    private utilityService: UtilityService,
    private agreementsService: AgreementsService,
    private navParams: NavParams,
    private getBudgetSupportTypesListQueryService: GetBudgetSupportTypesListQueryService,
    private minimumRateService: MinimumRateService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.agreement = this.navParams.get('agreement');
    this.isPrivateJob = this.navParams.get('isPrivateJob');
    this.isNewAgreement = this.statesForNew.includes(this.agreement.status);
    this.getMinimumAgreementRate();

    await this.getBudgetSupportTypesList();

    Promise.all([this.setProfileObject(), this.getOrganisationTerms(), this.getCoordinator()]).then(() => {
      this.setupFormFields();
    });
  }

  ngOnDestroy() {
    this.destroySub$.next(null);
    this.destroySub$.complete();
  }

  /**
   * Set the worker profile object.
   */
  private async setProfileObject(): Promise<void> {
    try {
      this.profileObject = await this.profileSearchService.getWorkerProfileProperties(true);
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Set up the initial form values.
   */
  private async setupFormFields(): Promise<void> {
    this.form = this.fb.group({
      rates: this.fb.array([]),
      is_travel_expense: [this.agreement?.travelExpenseFlag || null],
      per_km_travel_rate: [this.agreement?.perKmRateInDollars || null],
    });
  }

  /**
   * Convert rate to form group.
   *
   * @param rate - Rate.
   */
  private ratesToFormGroup(rate: Partial<RateType>): UntypedFormGroup {
    return this.fb.group({
      id: [rate.id],
      rateName: [rate.rateName, Validators.required],
      rateCents: [
        rate.rateCents,
        [
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
          Validators.min(rate?.flatRate ? 1 : this.minimumRate),
          Validators.max(9999.99),
        ],
      ],
      flatRate: [rate.flatRate],
      destroy: [rate.destroy],
      netRate: [rate.rateWithFee],
      exceedCapRate: [rate.exceedCapRate],
      showCapRateBanner: [rate.showCapRateBanner],
      priceLimitAlertType: [rate.priceLimitAlertType],
      dayOfWeekSelected: [rate.priceLimitAlertType],
      timeOfDaySelected: [rate.priceLimitAlertToDType],
      capRate: [rate.capRate],
      rateDay: [rate.rateDay],
      rateTime: [rate.rateTime],
      rateDescription: [rate.rateDescription],
      rateSchedule: [rate.rateSchedule],
      tempRateId: [rate.tempRateId],
      budgetSupportTypes: [rate.budgetSupportTypes],
      supportLocation: rate.supportLocation,
      supportType: rate.supportType,
      supportName: rate.supportName,
    });
  }

  /**
   * Transform rate data which is need for the mutation.
   *
   * @param rates - form rates value
   */
  private formatRates(rates: Rate[]): UntypedFormGroup[] {
    const ratesAttrs = [];

    rates.forEach((rate, i) => {
      ratesAttrs[i] = {
        id: rate.id,
        rateName: rate.rateName,
        rateDescription: rate.rateDescription,
        rateScheduleAttributes: rate.rateSchedule || DEFAULT_RATE_SCHEDULE,
        rateDollars: parseFloat(rate.rateCents.toString()),
        flatRate: rate.flatRate,
        _destroy: rate.destroy,
        tempRateId: rate.tempRateId,
      };

      if (this.shouldShowBudgetSupportTypeInformation) {
        ratesAttrs[i].budgetSupportTypes = rate.budgetSupportTypes;
      }
    });
    return ratesAttrs;
  }

  /**
   * Submit the agreement for private jobs.
   */
  async submitRequest(): Promise<void> {
    try {
      this.form.markAllAsTouched();
      if (this.form.valid && this.isRatesFormValid) {
        // check if it is private job or not
        if (!this.isPrivateJob) {
          await this.sendOffer();
          // continue to show popup for accepting terms and conditions only when
          // terms are not already accepted,
          // account type is 'Consumer'and client is co-ordinator managed
          // or independent and is Support at Home funded
          // show terms and conditions popup only when organisation has terms and conditions already otherwise continue to send agreement
        } else if (
          this.organisationTerms?.status !== 'accepted' &&
          this.agreement?.otherUser?.accountType === 'Consumer' &&
          this.organisationTerms?.isShowingTermPopup &&
          this.organisationTerms?.contentOfTerms
        ) {
          this.displayTermsModal(this.organisationTerms);
        } else {
          this.sendPrivateJobOffer();
        }
      }
    } catch (e) {
      this.utilityService.showErrorAlert('Error fetching terms. Please try again later.');
    }
  }

  /**
   * Open modal for the next step.
   * Setup dismiss event for that modal to process the return data and dismiss this modal and send the formatted data.
   */
  private async sendOffer(): Promise<void> {
    if (this.form.valid) {
      this.modalCtrl
        .create({
          component: AgreementFormStep4Component,
          componentProps: { agreement: this.agreement },
          cssClass: 'fullpage-modal',
        })
        .then((modal) => {
          // https://github.com/ionic-team/ionic-framework/issues/26872
          modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

          modal.onDidDismiss().then(async (response) => {
            if (response?.data) {
              // When the next modal is submitted and dismissed, merge the form data in that step into the formValues

              const formValues = JSON.parse(JSON.stringify(this.form.value));

              formValues.rates = this.formatRates(this.form.value.rates);
              formValues.per_km_travel_rate =
                formValues.is_travel_expense && formValues.per_km_travel_rate ? +formValues.per_km_travel_rate : null;

              this.modalCtrl.dismiss(
                { ...formValues, ...response.data, workerType: this.profileObject.myType },
                undefined,
                'agreement-step-3',
              );
            }
          });
          modal.present();
        });
    }
  }

  /**
   * Set the is_travel_expense and per_km_travel_rate on the form to the values emitted from the agreement travel form.
   * Also set the errors on the per_km_travel_rate if it exists.
   *
   * @param travelRateValues - the travel rate values emitted from the agreement travel form.
   */
  onTravelRateChange(travelRateValues: AgreementTravelFormOutput): void {
    this.form.controls.is_travel_expense.setValue(travelRateValues.includeTravelRate);
    this.form.controls.per_km_travel_rate.setValue(travelRateValues.perKmTravelRate);

    // set the errors on the per_km_travel_rate
    this.form.controls.per_km_travel_rate.setErrors(travelRateValues.perKmTravelRateErrors);
  }

  /**
   * Get the organisation terms to be used on form submission & to display the terms for private jobs.
   */
  private async getOrganisationTerms(): Promise<void> {
    if (!this.isPrivateJob) return;

    try {
      this.organisationTerms = await this.agreementsService.getOrganisationTerms(this.agreement.otherUser?.id);
    } catch (e) {
      this.utilityService.showErrorAlert('Error fetching terms. Please try again later.');
    }
  }

  /**
   * Get the coordinator from the agreement to display on the terms for private jobs.
   */
  private async getCoordinator(): Promise<void> {
    if (!this.isPrivateJob) return;

    try {
      if (this.agreement?.id && this.agreement?.otherUser?.accountType === 'Consumer') {
        this.agreement.otherUser.coordinator = await this.agreementsService.getCoordinator(this.agreement.id);
      }
    } catch (err) {
      this.utilityService.showErrorAlert('Error while fetching coordinator details. Please try again later.');
    }
  }

  /**
   * Open terms and conditions modal popup
   *
   * @param terms - Contains Organisation modal and pass details into TermsAndConditions modal
   */
  async displayTermsModal(terms: Organisation): Promise<void> {
    const termsModal = await this.modalCtrl.create({
      component: TermsAndConditionsComponent,
      componentProps: {
        terms,
        action: this.agreement.conversation?.hasAgreement ? 'edit' : 'add',
        isAgreementRequested: this.agreement.status === 'requested',
      },
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    termsModal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    await termsModal.present();
    const { data } = await termsModal.onDidDismiss();
    // Allow to send offer only when either the terms were accpeted
    // or were cancelled while amending existing agreement that was not requested by the client
    // because in while offering new agreement or ameneding the requested agreement restricting
    // SP to continue only after accepting organisation terms.
    if (
      data === 'accepted' ||
      (data === 'cancelled' && this.agreement?.conversation?.hasAgreement && this.agreement.status !== 'requested')
    ) {
      setTimeout(() => {
        this.sendPrivateJobOffer();
      }, 300);
    }
  }

  /**
   * Send Private job offer.
   */
  private sendPrivateJobOffer(): void {
    if (this.form.valid) {
      const formValues = JSON.parse(JSON.stringify(this.form.value));
      formValues.rates = this.formatRates(this.form.value.rates);
      formValues.per_km_travel_rate = +formValues.per_km_travel_rate;
      this.modalCtrl.dismiss(formValues);
    }
  }

  /**
   * Close modal
   */
  closeModal(): void {
    this.modalCtrl.dismiss(false);
  }

  /**
   * Opens the interruptive exceeded rate modal and submit the request in case SP selects a reason why
   * the rates exceed NDIS price limit.
   *
   * @param exceededCapRates - array containing the rates that exceed NDIS price limit.
   */
  private async openExceededRateModal(exceededCapRates: ExceededRate[]): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: AgreementInterruptiveModalComponent,
      componentProps: {
        exceededCapRates,
        mableClientFee: this.agreement?.grandfatheredAt ? MABLE_CLIENT_FEES.legacyFee : MABLE_CLIENT_FEES.currentFee,
        carerId: this.profileObject?.user?.id,
        carerUuid: this.profileObject?.user?.uuid,
        consumerId: this.agreement?.otherUser?.id,
        consumerUuid: this.agreement?.otherUser?.uuid,
      },
      cssClass: 'fullpage-modal',
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    modal.onDidDismiss().then((response) => {
      // check if SP clicked on button 'Continue with current rates'
      if (response.data === 'continue') {
        this.submitRequest();
      }
    });
    modal.present();
  }

  /**
   * Extracts info about and group the rates that exceeds NDIS max amount and are not flat rates within the current agreement.
   *
   * @returns array that contains info about the rates that exceeds NDIS max amount within the current agreement.
   */
  private getExceededCapRates(): ExceededRate[] {
    const exceededCapRates: ExceededRate[] = [];
    (this.form?.controls?.rates as FormArray).controls.forEach((control) => {
      if (control.value.exceedCapRate && !control.value.flatRate) {
        exceededCapRates.push({
          name: control.value.rateName,
          amount: control.value.rateCents,
          ndisMaxAmount: control.value.capRate,
        });
      }
    });
    return exceededCapRates;
  }

  /**
   * Function that is triggered once the 'Next' button is clicked.
   *
   * First check if the rates form is valid before continuing.
   *
   * Then evaluates whether there are rates that exceed NDIS price limit, and if client is a budget user, if so,
   * calls function to open the interruptive exceeded rate modal. Else, proceeds to submit the request.
   */
  onNext(): Promise<void> {
    this.hasSubmitted = true;
    if (!this.isRatesFormValid) return;

    // check if there are any rates exceeding the limit and if so, display exceeded rate modal
    const exceededCapRates = this.getExceededCapRates();
    if (exceededCapRates.length && this.shouldShowBudgetSupportTypeInformation) {
      this.openExceededRateModal(exceededCapRates);
    } else {
      this.submitRequest();
    }
  }

  /**
   * Patch the rates from the agreement rates child component and set the valid status.
   * Update the hasSubmitted flag to false.
   *
   * @param rateChange - value change event emitted from the agreement rates component.
   */
  onRatesChange(rateChange: AgreementRatesFormOutput): void {
    this.patchRatesToFormArray(rateChange.rates);
    this.isRatesFormValid = rateChange.status;
    this.hasSubmitted = false;
  }

  /**
   * Patch the given rates to the rates array.
   *
   * @param rates - rate values from the agreement rates form.
   */
  private patchRatesToFormArray(rates: Partial<RateType>[]): void {
    const ratesArray = this.form.get('rates') as FormArray;
    ratesArray.clear();

    rates.forEach((rate) => {
      ratesArray.push(this.ratesToFormGroup(rate));
    });
  }

  /**
   * Sets the minimum agreement rate.
   */
  private getMinimumAgreementRate(): void {
    this.minimumRateService
      .fetchMinimumAgreementRate$()
      .pipe(takeUntil(this.destroySub$))
      .subscribe((minimumRate: number) => {
        this.minimumRate = minimumRate;
      });
  }

  /**
   * Fetches The budget support types list.
   */
  private async getBudgetSupportTypesList(): Promise<void> {
    const consumerUserId = this.agreement.otherUser.id.toString();
    try {
      await firstValueFrom(
        this.getBudgetSupportTypesListQueryService
          .fetch({
            consumerUserId,
            postedJobId: this.agreement?.job?.id.toString(),
          })
          .pipe(
            map((response) => response.data.budgetSupportTypes),
            tap((budgetSupportTypes) => {
              this.shouldShowBudgetSupportTypeInformation = budgetSupportTypes.availableBudgetSupportTypes.length > 0;
              this.budgetSupportTypes = {
                availableBudgetSupportTypes: budgetSupportTypes.availableBudgetSupportTypes,
                defaultBudgetSupportTypes: budgetSupportTypes.defaultBudgetSupportTypes,
              };
            }),
          ),
      );
    } catch (error) {
      console.error('[AgreementFormStep3Component] Error fetching the budget support types list.', error);
    }
  }
}
